import React from 'react';
import '../styles/Skeleton.css';

const joinClasses = (...values) => values.filter(Boolean).join(' ');

export const SkeletonBlock = ({ className = '' }) => (
  <div
    aria-hidden="true"
    className={joinClasses('rounded-lg bg-gray-200 motion-safe:animate-pulse dark:bg-gray-700', className)}
  />
);

export const AppShellSkeleton = () => (
  <div className="min-h-screen bg-gray-50 p-4 dark:bg-slate-950" role="status" aria-label="Загрузка приложения">
    <div className="mx-auto flex min-h-[calc(100vh-2rem)] max-w-6xl gap-4">
      <aside className="hidden w-64 shrink-0 space-y-4 rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900 md:block">
        <SkeletonBlock className="h-10 w-40" />
        <div className="space-y-2 pt-3">
          {[...Array(7)].map((_, index) => <SkeletonBlock key={index} className="h-11 w-full" />)}
        </div>
      </aside>
      <main className="min-w-0 flex-1 space-y-4">
        <div className="flex items-center justify-between rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900">
          <SkeletonBlock className="h-8 w-44" />
          <SkeletonBlock className="h-9 w-9 rounded-full" />
        </div>
        <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900">
          <SkeletonBlock className="mb-4 h-10 w-full" />
          <div className="space-y-3">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="rounded-xl border border-gray-200 p-4 dark:border-gray-800">
                <div className="flex items-center gap-3">
                  <SkeletonBlock className="h-10 w-10 shrink-0 rounded-xl" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <SkeletonBlock className="h-4 w-2/5" />
                    <SkeletonBlock className="h-3 w-3/5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  </div>
);

export const CompactListSkeleton = ({ count = 4, showLeading = true, className = '' }) => (
  <div className={joinClasses('space-y-2', className)} role="status" aria-label="Загрузка списка">
    {[...Array(count)].map((_, index) => (
      <div key={index} className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center gap-3">
          {showLeading ? <SkeletonBlock className="h-9 w-9 shrink-0 rounded-xl" /> : null}
          <div className="min-w-0 flex-1 space-y-2">
            <SkeletonBlock className="h-3.5 w-2/5" />
            <SkeletonBlock className="h-3 w-3/4" />
          </div>
          <SkeletonBlock className="h-7 w-16 shrink-0 rounded-full" />
        </div>
      </div>
    ))}
  </div>
);

export const ModalListSkeleton = ({ count = 4 }) => (
  <div className="space-y-2 py-1" role="status" aria-label="Загрузка содержимого">
    {[...Array(count)].map((_, index) => (
      <div key={index} className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <SkeletonBlock className="h-10 w-10 shrink-0 rounded-xl" />
          <div className="min-w-0 flex-1 space-y-2">
            <SkeletonBlock className="h-4 w-1/2" />
            <SkeletonBlock className="h-3 w-4/5" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export const MapLoadingSkeleton = ({ className = '' }) => (
  <div
    className={joinClasses('absolute inset-0 z-[1] overflow-hidden bg-gray-100 dark:bg-gray-800', className)}
    role="status"
    aria-label="Загрузка карты"
  >
    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-gray-100 to-emerald-50 motion-safe:animate-pulse dark:from-slate-900 dark:via-gray-800 dark:to-slate-900" />
    <div className="absolute left-[12%] top-[24%] h-2 w-[58%] rotate-6 rounded-full bg-white/80 dark:bg-gray-700/70" />
    <div className="absolute bottom-[24%] right-[8%] h-2 w-[64%] -rotate-12 rounded-full bg-white/80 dark:bg-gray-700/70" />
    <div className="absolute left-1/2 top-1/2 h-10 w-8 -translate-x-1/2 -translate-y-1/2 rounded-t-full rounded-b-[55%] bg-blue-500/75 shadow-lg" />
  </div>
);

// Скелет для списка пользователей
export const UsersListSkeleton = ({ count = 5 }) => {
  return (
    <div className="space-y-3">
      {[...Array(count)].map((_, index) => (
        <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
            <div className="flex-1">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2 animate-pulse"></div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 animate-pulse"></div>
            </div>
            <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Скелет для деталей пользователя
export const UserDetailsSkeleton = () => {
  return (
    <div className="space-y-4">
      {/* Заголовок */}
      <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow-sm">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          <div className="flex-1">
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 animate-pulse"></div>
          </div>
        </div>
      </div>
      
      {/* Основная информация */}
      <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow-sm">
        <div className="space-y-4">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4 animate-pulse"></div>
          
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-1 animate-pulse"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 animate-pulse"></div>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-20 animate-pulse"></div>
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-24 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Этапы и меню */}
      <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow-sm">
        <div className="space-y-4">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4 animate-pulse"></div>
          
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2 animate-pulse"></div>
            <div className="flex flex-wrap gap-2">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-24 animate-pulse"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-20 animate-pulse"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-28 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Скелет для списка подразделений
export const DepartmentsListSkeleton = ({ count = 3 }) => {
  return (
    <div className="space-y-3">
      {[...Array(count)].map((_, index) => (
        <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                {/* Делаем иконку с градиентом и анимацией */}
                <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg animate-pulse"></div>
                <div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-1 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32 animate-pulse"></div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded-full w-16 animate-pulse"></div>
              </div>
            </div>
            <div className="flex space-x-1 ml-3">
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
